import { automationTimestampsFragment } from './fragments';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

// ========================================
// Types
// ========================================

export type ApprovalStatus = 'Pending' | 'Approved' | 'Rejected' | 'Expired';

export type ApprovalSortOption =
  | 'RequestedAtAsc'
  | 'RequestedAtDesc'
  | 'ExpiresAtAsc'
  | 'ExpiresAtDesc';

export type ApprovalRequestRecord = {
  id: string;
  executionRunId: string;
  automationId: string;
  organizationId: string;
  nodeId: string;
  nodeLabel: string;
  status: ApprovalStatus;
  instructions: string | null;
  executionData: Record<string, unknown> | null;
  requestedAt: string;
  expiresAt: string | null;
  respondedAt: string | null;
  respondedBy: string | null;
  comment: string | null;
  timestamps: {
    createdAt: string;
    updatedAt: string;
  };
};

export type ApprovalRequestFilter = {
  status?: ApprovalStatus[];
  automationId?: string;
  executionRunId?: string;
};

export type ApprovalRequestRespondInput = {
  approved: boolean;
  comment?: string | null;
};

// ========================================
// Fragments
// ========================================

export const approvalRequestFragment = {
  id: true,
  executionRunId: true,
  automationId: true,
  organizationId: true,
  nodeId: true,
  nodeLabel: true,
  status: true,
  instructions: true,
  executionData: true,
  requestedAt: true,
  expiresAt: true,
  respondedAt: true,
  respondedBy: true,
  comment: true,
  timestamps: automationTimestampsFragment,
} as const;

// ========================================
// Query: List Pending Approvals
// ========================================

export type PendingApprovalsListParams = {
  status?: ApprovalStatus[];
  automationId?: string;
  executionRunId?: string;
  sorting?: ApprovalSortOption[];
  limit?: number;
  offset?: number;
};

export const pendingApprovalsListQueryAtom = createQueryAtomFactory(
  'automations.approvals.list',
  (params: PendingApprovalsListParams) => ({
    automationsPendingApprovals: {
      __args: {
        filter: {
          status: params.status,
          automationId: params.automationId,
          executionRunId: params.executionRunId,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: approvalRequestFragment,
    },
  }),
  {
    select: ({
      automationsPendingApprovals,
    }): { total: number; approvals: ApprovalRequestRecord[] } => ({
      approvals: automationsPendingApprovals.entries.map(
        (approval: ApprovalRequestRecord) => ({
          ...approval,
          timestamps: {
            createdAt: approval.timestamps.createdAt,
            updatedAt: approval.timestamps.updatedAt,
          },
        }),
      ),
      total: automationsPendingApprovals.page.total ?? 0,
    }),
  },
);

// ========================================
// Query: Single Approval Request
// ========================================

export const approvalRequestQueryAtom = createQueryAtomFactory(
  'automations.approvals.detail',
  (params: { id: string }) => ({
    automationsApprovalRequest: {
      __args: {
        id: params.id,
      },
      ...approvalRequestFragment,
    },
  }),
  {
    select: ({ automationsApprovalRequest }): ApprovalRequestRecord | null => {
      if (!automationsApprovalRequest) return null;
      return {
        ...automationsApprovalRequest,
        timestamps: {
          createdAt: automationsApprovalRequest.timestamps.createdAt,
          updatedAt: automationsApprovalRequest.timestamps.updatedAt,
        },
      };
    },
  },
);

// ========================================
// Mutation: Respond to Approval
// ========================================

export const approvalRespondAtom = createMutationAtomFactory(
  'automations.approvals.respond',
  (params: { id: string; input: ApprovalRequestRespondInput }) => ({
    automationsApprovalRespond: {
      __args: {
        id: params.id,
        input: params.input,
      },
      ...approvalRequestFragment,
    },
  }),
);
